import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Loader2, Search, User, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const maskPhone = (phone) => {
  const digits = (phone || '').replace(/\D/g, '');
  if (digits.length < 4) return phone || '—';
  return `${digits.slice(0, 2)}${'•'.repeat(Math.max(0, digits.length - 4))}${digits.slice(-2)}`;
};

/**
 * Apply an open client_wallet_credit note — to the SAME traveller or to a
 * DIFFERENT traveller's booking (family member use). Search is always via
 * autocomplete (name/phone/UCC) — no manual UCC typing. Cross-traveller
 * apply requires a typed relationship + reason, shown via a confirmation
 * card (name, UCC, masked phone) before submission.
 */
const ApplyCreditDialog = ({ creditNote, open, onOpenChange, onDone }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState(null);       // { ucc, fullName, phone }
  const [targetTrips, setTargetTrips] = useState([]);
  const [tripId, setTripId] = useState('');
  const [amount, setAmount] = useState('');
  const [relationship, setRelationship] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery(''); setResults([]); setTarget(null); setTargetTrips([]); setTripId('');
    setAmount(''); setRelationship(''); setReason('');
  }, [open]);

  useEffect(() => {
    if (!query.trim() || target) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${BACKEND_URL}/api/clients`, { params: { search: query }, withCredentials: true });
        setResults((r.data || []).slice(0, 8));
      } catch (err) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, target]);

  const selectTarget = useCallback(async (client) => {
    setTarget(client);
    setResults([]);
    setQuery(client.fullName || client.ucc);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/clients/${client.ucc}/trips`, { withCredentials: true });
      setTargetTrips(r.data || []);
    } catch (err) {
      setTargetTrips([]);
    }
  }, []);

  const clearTarget = () => { setTarget(null); setQuery(''); setTargetTrips([]); setTripId(''); };

  const isCrossTraveller = target && creditNote && target.ucc !== creditNote.clientUCC;
  const amt = Number(amount) || 0;
  const remaining = Number(creditNote?.remainingAmount || 0);
  const overLimit = amt > remaining + 0.01;

  const handleSubmit = async () => {
    if (!target) { toast.error('Select a traveller'); return; }
    if (!tripId) { toast.error('Select a booking to apply the credit against'); return; }
    if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
    if (overLimit) { toast.error('Amount exceeds the remaining credit balance'); return; }
    if (isCrossTraveller && !relationship.trim()) { toast.error('Relationship is required for a different traveller'); return; }
    if (isCrossTraveller && !reason.trim()) { toast.error('Reason is required for a different traveller'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/credit-notes/wallet/${creditNote.creditNoteId}/apply`, {
        amount: amt,
        appliedToType: 'trip',
        appliedToReferenceId: tripId,
        targetTravellerUCC: target.ucc,
        relationship: relationship.trim(),
        reason: reason.trim(),
      }, { withCredentials: true });
      toast.success('Credit applied');
      onOpenChange(false);
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply credit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="apply-credit-dialog">
        <DialogHeader>
          <DialogTitle>Apply Wallet Credit</DialogTitle>
          <DialogDescription>
            {creditNote ? `${creditNote.creditNoteNumber} · ${inr(creditNote.remainingAmount)} available` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div>
            <Label>Apply to traveller</Label>
            {!target ? (
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, phone or UCC…"
                  className="pl-8"
                  data-testid="apply-credit-search-input"
                />
                {searching && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-slate-400" />}
                {results.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-56 overflow-y-auto" data-testid="apply-credit-search-results">
                    {results.map((c) => (
                      <button
                        key={c.ucc}
                        type="button"
                        onClick={() => selectTarget(c)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100 last:border-0"
                        data-testid={`apply-credit-result-${c.ucc}`}
                      >
                        <User className="h-3.5 w-3.5 text-slate-400" />
                        <div>
                          <div className="font-medium text-slate-800">{c.fullName}</div>
                          <div className="text-[11px] text-slate-400">{c.displayUCC || c.ucc} · {maskPhone(c.phone)}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-1 rounded-lg border border-teal-200 bg-teal-50 p-3 flex items-center justify-between" data-testid="apply-credit-target-card">
                <div>
                  <div className="font-semibold text-slate-800">{target.fullName}</div>
                  <div className="text-[11px] text-slate-500">{target.displayUCC || target.ucc} · {maskPhone(target.phone)}</div>
                  {isCrossTraveller && (
                    <div className="text-[11px] text-amber-700 font-medium mt-0.5">Different traveller from credit owner — relationship required</div>
                  )}
                </div>
                <Button size="icon" variant="ghost" onClick={clearTarget} data-testid="apply-credit-clear-target-btn">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {target && (
            <div>
              <Label>Booking to apply against</Label>
              <Select value={tripId} onValueChange={setTripId}>
                <SelectTrigger className="mt-1" data-testid="apply-credit-trip-select"><SelectValue placeholder="Select a booking…" /></SelectTrigger>
                <SelectContent>
                  {targetTrips.map((t) => (
                    <SelectItem key={t.tripId} value={t.tripId}>
                      {t.displayTripId || t.tripId} · {t.destination} · {t.status}
                    </SelectItem>
                  ))}
                  {targetTrips.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-400">No bookings found for this traveller</div>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Amount to apply (₹)</Label>
            <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="apply-credit-amount-input" className="mt-1" />
            {overLimit && <p className="text-xs text-rose-600 mt-1">Exceeds remaining credit of {inr(remaining)}</p>}
          </div>

          {isCrossTraveller && (
            <div>
              <Label>Relationship to {creditNote?.clientName}</Label>
              <Input value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="e.g. Spouse, Parent, Sibling" data-testid="apply-credit-relationship-input" className="mt-1" />
            </div>
          )}
          <div>
            <Label>Reason {isCrossTraveller ? '' : '(optional)'}</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this credit being applied here?" data-testid="apply-credit-reason-input" className="mt-1" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="apply-credit-cancel-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="apply-credit-submit-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply Credit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyCreditDialog;
